import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useIdea, useIdeaCatalogue, useIdeaRun } from '@/api/queries';
import type { Disposition, IdeaDetail, Research } from '@/api/types';
import { useSession } from '@/session/SessionContext';

import type { IdeaLinkState } from '../ideaUi';

export type Panel = 'submission' | 'history';
export type Confirm = { kind: 'rerun' } | { kind: 'disposition'; to: Disposition };

/** The page's own state: which dialog is open, whether the industry is being edited, and which earlier run is on screen. */
export interface IdeaDetailUi {
  panel: Panel | null;
  setPanel: (panel: Panel | null) => void;
  editingIndustry: boolean;
  setEditingIndustry: (editing: boolean) => void;
  viewing: string | null;
  setViewing: (id: string | null) => void;
  confirm: Confirm | null;
  setConfirm: (confirm: Confirm | null) => void;
  pastRun: ReturnType<typeof useIdeaRun>;
  canExport: boolean;
}

/** The idea, the catalogue it is scored against, where it was opened from, and the page's state. */
export function useIdeaDetail() {
  const { ideaId = '' } = useParams();
  const idea = useIdea(ideaId);
  const catalogue = useIdeaCatalogue();
  const location = useLocation();
  const { can } = useSession();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [editingIndustry, setEditingIndustry] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const pastRun = useIdeaRun(ideaId, viewing);
  // Back to where the idea was opened (view and filters kept), else the plain list.
  const from = (location.state as IdeaLinkState | null)?.from;
  const ui: IdeaDetailUi = {
    panel,
    setPanel,
    editingIndustry,
    setEditingIndustry,
    viewing,
    setViewing,
    confirm,
    setConfirm,
    pastRun,
    // The export needs both: the server answers 403 to idea.export without idea.view.
    canExport: can('idea.view') && can('idea.export'),
  };
  return { idea, catalogue, from, ui };
}

/** The research on screen: the earlier run being viewed once it has loaded, else the current one. */
export function shownResearch(idea: IdeaDetail, ui: IdeaDetailUi): Research | null {
  return ui.viewing ? (ui.pastRun.data ?? null) : idea.current_research;
}
